/**
 * PresenterAudioManager — centralized audio for the presenter.
 *
 * Sound subsystem: Web Audio API (beep, box counter, unlock)
 * Music subsystem: MusicKit volume ducking with iOS fallback
 *
 * iOS audio session strategy
 * ──────────────────────────
 * iOS places all Web Audio API output in the "ambient" session category by
 * default, which is silenced by the hardware mute switch. Apple Music (MusicKit)
 * uses "playback" category and ignores the mute switch. As long as MusicKit is
 * playing, Web Audio piggybacks on the "playback" session and also works.
 *
 * Without MusicKit we need to force the session into "playback" ourselves.
 * The reliable technique: play a looping silent <audio> element from a user
 * gesture. A one-shot play() is not enough — iOS reverts to "ambient" once the
 * element ends. A looping element keeps the session in "playback" indefinitely.
 *
 * The silence content must be a real WAV with actual (though zero-amplitude)
 * samples. A 0-byte WAV (empty data chunk) is not treated as "playing audio"
 * by iOS and does not upgrade the session.
 */

import { soundUrl, loadArrayBuffer } from '../../lib/platform';

/**
 * Generates a data URL for a 100 ms silent WAV (8000 Hz, 8-bit mono).
 * Called once and cached via module-level variable.
 */
let _silenceDataUrl = null;
function getSilenceDataUrl() {
  if (_silenceDataUrl) return _silenceDataUrl;
  const rate = 8000;
  const samples = rate / 10; // 100 ms = 800 samples
  const buf = new ArrayBuffer(44 + samples);
  const v = new DataView(buf);
  const str = (off, s) => [...s].forEach((c, i) => v.setUint8(off + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);    // fmt chunk size
  v.setUint16(20, 1, true);     // PCM
  v.setUint16(22, 1, true);     // mono
  v.setUint32(24, rate, true);  // sample rate
  v.setUint32(28, rate, true);  // byte rate (rate × 1 ch × 1 byte/sample)
  v.setUint16(32, 1, true);     // block align
  v.setUint16(34, 8, true);     // bits per sample
  str(36, 'data');
  v.setUint32(40, samples, true);
  // 128 = silence for unsigned 8-bit PCM (0 → -1.0, 128 → 0.0, 255 → +1.0)
  new Uint8Array(buf, 44).fill(128);
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  _silenceDataUrl = 'data:audio/wav;base64,' + btoa(bin);
  return _silenceDataUrl;
}

export class PresenterAudioManager {
  // === Sound subsystem ===
  _audioCtx = null;
  _boxCounterBuffer = null;
  _breakBuffer = null;
  _startExerciseBuffer = null;
  _preloadAttempted = false;
  _preloadBreakAttempted = false;
  _preloadStartExerciseAttempted = false;

  // === iOS audio session keep-alive ===
  _sessionKeepAlive = null; // looping <audio> element — holds AVAudioSession in "playback"
  _keepAliveNode = null;    // looping Web Audio buffer — secondary protection

  // === Music subsystem ===
  _musicKit = null;
  _volumeWorks = null; // null = untested, true/false
  _currentMode = 'FULL'; // 'FULL' | 'DUCKED' | 'PAUSED_FALLBACK'
  _fadeTimer = null;
  _wasPlayingBeforeVideo = false;

  constructor() {
    // Re-resume AudioContext AND <audio> keep-alive when the user returns to
    // the app (screen unlock, app switcher, notification tap).
    this._onVisibilityChange = () => {
      if (!document.hidden) {
        if (this._audioCtx?.state === 'suspended') {
          this._audioCtx.resume().catch(() => {});
        }
        if (this._sessionKeepAlive?.paused) {
          this._sessionKeepAlive.play().catch(() => {});
        }
      }
    };
    // Fallback: any touch re-activates a suspended context after interruptions.
    this._onTouchStart = () => {
      if (this._audioCtx?.state === 'suspended') {
        this._audioCtx.resume().catch(() => {});
      }
      if (this._sessionKeepAlive?.paused) {
        this._sessionKeepAlive.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    document.addEventListener('touchstart', this._onTouchStart, { passive: true });
  }

  // ── Sound: AudioContext ──────────────────────────────────────────────

  _getCtx() {
    if (!this._audioCtx) {
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this._audioCtx;
  }

  /**
   * Must be called from a user gesture (click/tap) to unlock AudioContext on iOS.
   * Returns a Promise — await it before starting the workout so the context is
   * guaranteed to be in "running" state when the first sound fires.
   */
  async unlock() {
    // Start a looping silent <audio> element. This is the definitive iOS fix:
    // it locks the AVAudioSession into "playback" category for the lifetime of
    // the session, which means Web Audio API sounds play regardless of the
    // hardware mute switch — exactly the same as when Apple Music is running.
    // Must be called from a user gesture (we're inside the Start button handler).
    try {
      if (!this._sessionKeepAlive) {
        this._sessionKeepAlive = new Audio(getSilenceDataUrl());
        this._sessionKeepAlive.loop = true;
      }
      await this._sessionKeepAlive.play();
    } catch (e) {
      // ignore — will fall back to whatever session category iOS chose
    }

    // Resume Web Audio API context. Must be awaited so callers can rely on
    // "running" state. 4096-sample silent buffer confirms unlock on iOS.
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        await ctx.resume();
      }
      const silent = ctx.createBuffer(1, 4096, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = silent;
      src.connect(ctx.destination);
      src.start(0);
    } catch (e) {
      // ignore
    }

    this._startKeepAlive();
    this._preloadBoxCounter();
    this._preloadBreak();
    this._preloadStartExercise();
  }

  /**
   * Secondary Web Audio keep-alive: looping zero-amplitude buffer connected to
   * the AudioContext destination. Prevents the context from going idle between
   * sounds on platforms that don't fully honour the <audio> session approach.
   */
  _startKeepAlive() {
    if (this._keepAliveNode) return;
    try {
      const ctx = this._getCtx();
      const buf = ctx.createBuffer(1, 4096, ctx.sampleRate); // zero-filled = silence
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(ctx.destination);
      src.start(0);
      this._keepAliveNode = src;
    } catch (e) {
      // ignore
    }
  }

  _preloadBoxCounter() {
    if (this._preloadAttempted) return;
    this._preloadAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('boxcounter'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => {
          this._boxCounterBuffer = decoded;
        })
        .catch(() => {
          /* synthesized fallback will be used */
        });
    } catch (e) {
      // ignore
    }
  }

  _preloadBreak() {
    if (this._preloadBreakAttempted) return;
    this._preloadBreakAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('break'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => {
          this._breakBuffer = decoded;
        })
        .catch(() => {
          /* no fallback — sound simply won't play */
        });
    } catch (e) {
      // ignore
    }
  }

  _preloadStartExercise() {
    if (this._preloadStartExerciseAttempted) return;
    this._preloadStartExerciseAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('start_exercise'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => {
          this._startExerciseBuffer = decoded;
        })
        .catch(() => {
          /* no fallback */
        });
    } catch (e) {
      // ignore
    }
  }

  // ── Sound: Beep ──────────────────────────────────────────────────────

  /** Short 880 Hz beep — one per second in the last 10s of a rest step. */
  playBeep() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {}); // best-effort recovery; this tick is skipped
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);

      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {
      // AudioContext blocked or unavailable
    }
  }

  // ── Sound: Box Counter ───────────────────────────────────────────────

  /** Play preloaded box counter MP3, fall back to synthesized thuds. */
  playBoxCounter() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {});
        return;
      }

      if (this._boxCounterBuffer) {
        const source = ctx.createBufferSource();
        source.buffer = this._boxCounterBuffer;
        source.connect(ctx.destination);
        source.start(0);
      } else {
        this._playThudsSynthesized();
      }
    } catch (e) {
      this._playThudsSynthesized();
    }
  }

  /** Play break MP3 when a rest step begins. */
  playBreak() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running' || !this._breakBuffer) {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        return;
      }
      const source = ctx.createBufferSource();
      source.buffer = this._breakBuffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (e) {
      // ignore
    }
  }

  /** Play start_exercise MP3 when an exercise step begins. */
  playStartExercise() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running' || !this._startExerciseBuffer) {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        return;
      }
      const source = ctx.createBufferSource();
      source.buffer = this._startExerciseBuffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (e) {
      // ignore
    }
  }

  _playThudsSynthesized() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') return;
      const now = ctx.currentTime;
      [0, 0.7, 1.4].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(350, now + offset);
        osc.frequency.exponentialRampToValueAtTime(180, now + offset + 0.18);
        gain.gain.setValueAtTime(0.9, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.25);
        osc.start(now + offset);
        osc.stop(now + offset + 0.25);
      });
    } catch (e) {
      // ignore
    }
  }

  // ── Music: Init & Control ────────────────────────────────────────────

  /** Register MusicKit instance after initialization. */
  initMusic(kit) {
    console.debug('[AudioManager] initMusic() called, kit:', !!kit);
    this._musicKit = kit;
  }

  /** Load playlist into queue WITHOUT playing (safe on iOS, no user-gesture needed). Returns name or null. */
  async loadPlaylist(playlistId) {
    const kit = this._musicKit;
    if (!kit || !playlistId) return null;
    try {
      await kit.setQueue({ playlist: playlistId, startWith: 0 });
      return kit.queue?.name || null;
    } catch (e) {
      console.warn('Apple Music queue failed:', e);
      return null;
    }
  }

  /** Start a playlist by Apple Music playlist ID. MUST be called from a user gesture. Returns playlist name or null. */
  async startPlaylist(playlistId) {
    const kit = this._musicKit;
    console.debug('[AudioManager] startPlaylist() called with playlistId:', playlistId, 'kit ready:', !!kit);
    if (!kit || !playlistId) {
      console.debug('[AudioManager] startPlaylist() early exit: kit?', !!kit, 'playlistId?', !!playlistId);
      return null;
    }
    try {
      // Only set queue if not already loaded
      if (!kit.queue?.length) {
        console.debug('[AudioManager] Setting queue with playlist:', playlistId);
        await kit.setQueue({ playlist: playlistId, startWith: 0 });
      }
      console.debug('[AudioManager] Calling kit.play()');
      await kit.play();
      console.debug('[AudioManager] Playlist started successfully. Queue name:', kit.queue?.name);
      return kit.queue?.name || null;
    } catch (e) {
      console.error('[AudioManager] Apple Music playback failed:', e);
      return null;
    }
  }

  /** Pause music for a presenter_video exercise. Remembers if it was playing. */
  pauseForVideo() {
    const kit = this._musicKit;
    if (!kit) return;
    this._wasPlayingBeforeVideo = this.isPlaying();
    if (this._wasPlayingBeforeVideo) {
      kit.pause();
    }
  }

  /** Resume music after a presenter_video exercise ends (if it was playing before). */
  resumeAfterVideo() {
    const kit = this._musicKit;
    if (!kit) return;
    if (this._wasPlayingBeforeVideo) {
      kit.play().catch(() => {});
    }
    this._wasPlayingBeforeVideo = false;
  }

  togglePlayback() {
    const kit = this._musicKit;
    if (!kit) return;
    if (kit.playbackState === window.MusicKit?.PlaybackStates?.playing) {
      kit.pause();
    } else {
      kit.play().catch(() => {});
    }
  }

  stopMusic() {
    if (this._musicKit) {
      this._musicKit.stop();
    }
  }

  isPlaying() {
    return this._musicKit?.playbackState === window.MusicKit?.PlaybackStates?.playing;
  }

  // ── Music: Volume Ducking ────────────────────────────────────────────

  _testVolumeControl() {
    if (this._volumeWorks !== null) {
      console.debug('[AudioManager] Volume control test cached:', this._volumeWorks);
      return this._volumeWorks;
    }
    const kit = this._musicKit;
    if (!kit) {
      console.debug('[AudioManager] Volume control test: MusicKit not ready');
      return false; // don't cache — MusicKit might not be ready yet
    }
    try {
      const before = kit.volume;
      kit.volume = 0.5;
      const after = kit.volume;
      kit.volume = before;
      this._volumeWorks = Math.abs(after - 0.5) < 0.01;
      console.debug('[AudioManager] Volume control test:', {
        before,
        setTo: 0.5,
        after,
        works: this._volumeWorks,
        device: navigator.userAgent
      });
    } catch (e) {
      console.debug('[AudioManager] Volume control test failed:', e.message);
      this._volumeWorks = false;
    }
    return this._volumeWorks;
  }

  _fadeVolume(target, durationMs) {
    const kit = this._musicKit;
    if (!kit) return;
    if (this._fadeTimer) clearInterval(this._fadeTimer);
    const start = kit.volume ?? 1.0;
    const steps = 20;
    const interval = durationMs / steps;
    const delta = (target - start) / steps;
    let step = 0;
    this._fadeTimer = setInterval(() => {
      step++;
      kit.volume = Math.max(0, Math.min(1, start + delta * step));
      if (step >= steps) {
        clearInterval(this._fadeTimer);
        this._fadeTimer = null;
      }
    }, interval);
  }

  /** Reduce music volume (or pause on iOS). No-op if already ducked. */
  duckMusic() {
    const kit = this._musicKit;
    console.debug('[AudioManager] duckMusic() called, currentMode:', this._currentMode);
    if (!kit || this._currentMode !== 'FULL') {
      console.debug('[AudioManager] duckMusic() early exit: kit exists?', !!kit, 'currentMode:', this._currentMode);
      return;
    }

    if (this._testVolumeControl()) {
      console.debug('[AudioManager] Using volume fade (DUCKED mode)');
      this._fadeVolume(0.2, 400);
      this._currentMode = 'DUCKED';
    } else {
      console.debug('[AudioManager] Using pause/resume fallback (PAUSED_FALLBACK mode)');
      try {
        kit.pause();
        console.debug('[AudioManager] kit.pause() succeeded');
      } catch (e) {
        console.error('[AudioManager] kit.pause() failed:', e);
      }
      this._currentMode = 'PAUSED_FALLBACK';
    }
  }

  /** Restore music volume (or resume on iOS). No-op if already full. */
  unduckMusic() {
    const kit = this._musicKit;
    console.debug('[AudioManager] unduckMusic() called, currentMode:', this._currentMode);
    if (!kit || this._currentMode === 'FULL') {
      console.debug('[AudioManager] unduckMusic() early exit: kit exists?', !!kit, 'currentMode:', this._currentMode);
      return;
    }

    if (this._currentMode === 'DUCKED') {
      console.debug('[AudioManager] Restoring volume from DUCKED');
      this._fadeVolume(1.0, 600);
    } else if (this._currentMode === 'PAUSED_FALLBACK') {
      console.debug('[AudioManager] Resuming music from PAUSED_FALLBACK');
      try {
        kit.play().catch((e) => {
          console.error('[AudioManager] kit.play() failed:', e);
        });
      } catch (e) {
        console.error('[AudioManager] Unexpected error calling kit.play():', e);
      }
    }
    this._currentMode = 'FULL';
  }

  // ── Cleanup ──────────────────────────────────────────────────────────

  destroy() {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    document.removeEventListener('touchstart', this._onTouchStart);
    if (this._sessionKeepAlive) {
      this._sessionKeepAlive.pause();
      this._sessionKeepAlive.src = '';
      this._sessionKeepAlive = null;
    }
    if (this._keepAliveNode) {
      try { this._keepAliveNode.stop(); } catch (e) {}
      this._keepAliveNode = null;
    }
    if (this._fadeTimer) {
      clearInterval(this._fadeTimer);
      this._fadeTimer = null;
    }
    if (this._audioCtx) {
      this._audioCtx.close().catch(() => {});
      this._audioCtx = null;
    }
    this._musicKit = null;
  }
}
